import moment from 'moment';

import { i18n } from '../main';
import Validation from '../models/Validation';
import ValidationObject from '../models/ValidationObject';

/**
 * Validation service to validate all input fields
 */
export default class ValidationService {
  /**
   * validates a value based on the given type
   *
   * @param type - type of input field which will determine what validation function is used
   * @param value - value to validate
   * @param optional - if the value is optional, then an empty value is returned as valid
   * @returns whether the value as the given type is valid
   */
  public static validate(
    type: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    optional?: boolean,
  ): Validation {
    const mapping: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: (value: any, optional?: boolean) => Validation;
    } = {
      name: ValidationService.validateName,
      grade: ValidationService.validateGrade,
      date: ValidationService.validateDate,
      weight: ValidationService.validateWeight,
    };
    return {
      ...mapping[type](value, optional),
      ...(optional !== undefined && { optional }),
    };
  }

  /**
   * Validates every property in the request based on the validations object.
   *
   * @param validations - contains validation conditions as Validation objects
   * @param object - contains the data to be validated
   * @returns updated object of validations
   */
  public static validateObject(
    validations: ValidationObject,
    object: { [key: string]: unknown },
  ): ValidationObject {
    const propertiesToValidate = Object.keys(validations);
    propertiesToValidate.forEach((p) => {
      validations[p] = ValidationService.validate(
        validations[p].type,
        object[p],
        validations[p].optional,
      );
    });
    return validations;
  }

  /**
   * Checks a validation object if every property is valid.
   *
   * @param form object containing Validation objects
   * @returns whether all Validation objects are valid
   */
  public static isObjectValid(form: { [key: string]: Validation }): boolean {
    return Object.keys(form).every((k: string) => form[k].isValid);
  }

  /**
   * Validates a name.
   *
   * @param name - the name string to be validated
   * @param optional - if the value is optional, then an empty value is returned as valid
   * @returns whether the name string is valid or not
   */
  private static validateName(name: string, optional?: boolean): Validation {
    const isValid =
      optional && !name
        ? true
        : name !== undefined &&
          name !== null &&
          name.length > 0 &&
          name.length <= 255;
    return {
      type: 'name',
      isValid,
      message: isValid ? null : i18n.tc('forms.validation.invalidName'),
    };
  }

  /**
   * Validates a grade.
   *
   * @param grade - the grade to be validated
   * @param optional - if the value is optional, then an empty value is returned as valid
   * @returns whether the grade is valid or not
   */
  private static validateGrade(grade: number, optional?: boolean): Validation {
    const isValid =
      optional && !grade
        ? true
        : grade !== undefined &&
          grade !== null &&
          typeof grade === 'number' &&
          !isNaN(grade) &&
          grade >= 1 &&
          grade <= 6;
    return {
      type: 'grade',
      isValid,
      message: isValid ? null : i18n.tc('forms.validation.invalidGrade'),
    };
  }

  /**
   * Validates a date.
   *
   * @remarks
   * Uses moment module to perform the validation
   *
   * @param date - the date to be validated
   * @param optional - if the value is optional, then an empty value is returned as valid
   * @returns whether the date is valid or not
   */
  private static validateDate(date: string, optional?: boolean): Validation {
    const isValid =
      optional && !date ? true : moment(date, 'DD.MM.YYYY').isValid();
    return {
      type: 'date',
      isValid,
      message: isValid ? null : i18n.tc('forms.validation.invalidDate'),
    };
  }

  /**
   * Validates a weight.
   *
   * @param date - the date to be validated
   * @param optional - if the value is optional, then an empty value is returned as valid
   * @returns whether the date is valid or not
   */
  private static validateWeight(
    weight: string,
    optional?: boolean,
  ): Validation {
    const isValid =
      optional && !weight
        ? true
        : weight !== undefined &&
          weight !== null &&
          typeof weight === 'number' &&
          !isNaN(weight) &&
          weight > 0 &&
          weight <= 10;
    return {
      type: 'weight',
      isValid,
      message: isValid ? null : i18n.tc('forms.validation.invalidWeight'),
    };
  }
}
